'use client';

import React, { useEffect, useRef, useState } from 'react';
import { BRAND_GREEN } from '@voley/shared-types/constants';

interface Props {
  primaryColor: string;
  width?: string | number;
  height?: string | number;
}

export default function TeamCollaborationSvg({ 
  primaryColor, 
  width = "100%", 
  height = 300 
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Use Intersection Observer to trigger animation when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio > 0.3) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (svgRef.current) {
      observer.observe(svgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @keyframes flowLine {
          0% {
            stroke-dashoffset: 30;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }

        @keyframes flowLineReverse {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: 30;
          }
        }

        @keyframes pulseGlow {
          0%, 100% {
            opacity: 0.3;
            filter: blur(0px);
          }
          50% {
            opacity: 0.8;
            filter: blur(2px);
          }
        }

        .connection-line {
          stroke-dasharray: 8, 6;
          stroke-linecap: round;
        }

        .connection-line-animated {
          animation: flowLine 0.8s linear infinite;
        }

        .connection-line-animated-reverse {
          animation: flowLineReverse 0.8s linear infinite;
        }

        .connection-line-glow {
          stroke-dasharray: 8, 6;
          stroke-linecap: round;
          animation: pulseGlow 2s ease-in-out infinite;
        }
      `}</style>
      <svg 
        ref={svgRef}
        width={width} 
        height={height} 
        viewBox="0 0 500 300" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Arrowhead definitions - smaller */}
          <marker
            id="arrowRight"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill={primaryColor} />
          </marker>
          <marker
            id="arrowLeft"
            markerWidth="8"
            markerHeight="6"
            refX="0"
            refY="3"
            orient="auto"
          >
            <polygon points="8 0, 0 3, 8 6" fill={primaryColor} />
          </marker>
          
          {/* Gradient for glowing lines */}
          <linearGradient id="lineGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="0.4" />
            <stop offset="50%" stopColor={primaryColor} stopOpacity="1" />
            <stop offset="100%" stopColor={primaryColor} stopOpacity="0.4" />
          </linearGradient>
          
          {/* Filter for glow effect */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Connection lines - glow layer (behind) */}
        {isVisible && (
          <g className="connection-line-glow" filter="url(#glow)">
            {/* Line 1: Team member 1 (150,230 r=40) to Team member 2 (350,150 r=28) */}
            <line x1="187" y1="215" x2="324" y2="160" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
            {/* Line 2: Team member 3 (180,60 r=18) to Team member 2 (350,150 r=28) */}
            <line x1="196" y1="69" x2="325" y2="137" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
            {/* Line 3: Team member 3 (180,60 r=18) to Team member 1 (150,230 r=40) */}
            <line x1="168" y1="72" x2="143" y2="200" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
          </g>
        )}

        {/* Connection arrows - animated dashes with correct edge connection points */}
        {/* Line 1: Team member 1 (150,230 r=40) to Team member 2 (350,150 r=28) - connects from edge to edge */}
        <line 
          x1="187" y1="215" x2="324" y2="160" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated' : ''}`}
          markerEnd="url(#arrowRight)" 
          markerStart="url(#arrowLeft)"
          style={{ animationDelay: '0s' }}
        />
        
        {/* Line 2: Team member 3 (180,60 r=18) to Team member 2 (350,150 r=28) - connects from edge to edge */}
        <line 
          x1="196" y1="69" x2="325" y2="137" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated-reverse' : ''}`}
          markerEnd="url(#arrowRight)" 
          markerStart="url(#arrowLeft)"
          style={{ animationDelay: '0.2s' }}
        />
        
        {/* Line 3: Team member 3 (180,60 r=18) to Team member 1 (150,230 r=40) - connects from edge to edge */}
        <line 
          x1="168" y1="72" x2="143" y2="200" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated' : ''}`}
          markerEnd="url(#arrowRight)" 
          markerStart="url(#arrowLeft)"
          style={{ animationDelay: '0.4s' }}
        />

        {/* Team member 1 (front - larger) */}
        <g>
          <circle cx="150" cy="230" r="40" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1" />
          <circle cx="150" cy="215" r="16" fill="#dddddd" />
          <rect x="128" y="240" width="44" height="22" rx="11" fill="#dddddd" />
        </g>
        
        {/* Team member 2 (middle right) */}
        <g>
          <circle cx="350" cy="150" r="28" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1" />
          <circle cx="350" cy="138" r="11" fill="#dddddd" />
          <rect x="335" y="155" width="30" height="16" rx="8" fill="#dddddd" />
        </g>
        
        {/* Team member 3 (back left - smaller) */}
        <g>
          <circle cx="180" cy="60" r="18" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1" />
          <circle cx="180" cy="54" r="7" fill="#dddddd" />
          <rect x="172" y="64" width="16" height="9" rx="4.5" fill="#dddddd" />
        </g>

        {/* Animated message particles flowing on lines */}
        {isVisible && (
          <>
            {/* Particle on line 1 (Team member 1 to Team member 2) */}
            <circle r="4" fill={primaryColor}>
              <animateMotion
                dur="1.2s"
                repeatCount="indefinite"
                path="M187,215 L324,160"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.2s" repeatCount="indefinite" />
            </circle>
            
            {/* Reverse particle on line 1 */}
            <circle r="3" fill={BRAND_GREEN}>
              <animateMotion
                dur="1.4s"
                repeatCount="indefinite"
                path="M324,160 L187,215"
                begin="0.6s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.4s" repeatCount="indefinite" begin="0.6s" />
            </circle>
            
            {/* Particle on line 2 (Team member 3 to Team member 2) */}
            <circle r="4" fill={primaryColor}>
              <animateMotion
                dur="1.5s"
                repeatCount="indefinite"
                path="M196,69 L325,137"
                begin="0.3s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.5s" repeatCount="indefinite" begin="0.3s" />
            </circle>
            
            {/* Reverse particle on line 2 */}
            <circle r="3" fill={BRAND_GREEN}>
              <animateMotion
                dur="1.6s"
                repeatCount="indefinite"
                path="M325,137 L196,69"
                begin="0.9s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.6s" repeatCount="indefinite" begin="0.9s" />
            </circle>
            
            {/* Particle on line 3 (Team member 3 to Team member 1) */}
            <circle r="4" fill={primaryColor}>
              <animateMotion
                dur="1.3s"
                repeatCount="indefinite"
                path="M168,72 L143,200"
                begin="0.5s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.3s" repeatCount="indefinite" begin="0.5s" />
            </circle>
            
            {/* Reverse particle on line 3 */}
            <circle r="3" fill={BRAND_GREEN}>
              <animateMotion
                dur="1.5s"
                repeatCount="indefinite"
                path="M143,200 L168,72"
                begin="1s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.5s" repeatCount="indefinite" begin="1s" />
            </circle>
          </>
        )}
      </svg>
    </>
  );
}
